import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

interface EditProfilePageProps {
  onBack: () => void;
}

const EditProfilePage: React.FC<EditProfilePageProps> = ({ onBack }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setError(null); // Limpiar errores previos
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No se encontró el token de autenticación');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setFirstName(data.data.firstName || '');
          setLastName(data.data.lastName || '');
          setError(null); // Limpiar errores si la carga fue exitosa
        } else {
          setError(data.message || 'Error al cargar el perfil');
        }
      } else {
        let errorMessage = 'Error al cargar el perfil';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Si no se puede parsear el error (por ejemplo, si es HTML)
          if (response.status === 404) {
            errorMessage = 'Endpoint no encontrado. Verifica que el servidor esté corriendo y reiniciado.';
          } else if (response.status === 401) {
            errorMessage = 'No autorizado. Por favor, inicia sesión nuevamente.';
          } else {
            errorMessage = `Error del servidor (${response.status}). Verifica que el servidor esté corriendo.`;
          }
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError('Error de conexión al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No se encontró el token de autenticación');
        setSaving(false);
        return;
      }

      // Limpiar valores vacíos
      const cleanFirstName = firstName.trim() || null;
      const cleanLastName = lastName.trim() || null;

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: cleanFirstName,
          lastName: cleanLastName
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccess('Perfil actualizado exitosamente');
          // Actualizar los valores con los datos del servidor
          setFirstName(data.data.firstName || '');
          setLastName(data.data.lastName || '');
        } else {
          setError(data.message || 'Error al actualizar el perfil');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || errorData.error || 'Error al actualizar el perfil');
      }
    } catch (err) {
      setError('Error de conexión al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-blue-600">Cargando perfil...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-2 pb-4 max-w-2xl">
      <div className="mb-4">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 mb-3 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Editar Perfil</h1>
        <p className="text-gray-600 mt-1">Actualiza tu información personal</p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-3">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nombre
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              placeholder="Tu nombre"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Apellido
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              placeholder="Tu apellido"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfilePage;

